import React, { useState } from "react";
import { Button } from "../../ui";
import { AssetTableItem, CampaignAsset } from "../../../types/types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEllipsisV, faEdit, faEye, faUsers, faPlus } from "@fortawesome/free-solid-svg-icons";

import { IconDefinition } from "@fortawesome/fontawesome-svg-core";

export interface AssetAction {
  label: string;
  icon?: IconDefinition;
  onClick: () => void;
  variant?: "primary" | "secondary" | "danger" | "ghost";
  className?: string;
  disabled?: boolean;
}

export interface AssetActionsProps {
  /** The asset data */
  asset: AssetTableItem | CampaignAsset;
  /** Custom actions */
  actions?: AssetAction[];
  /** Display variant */
  variant?: "default" | "campaign" | "tree";
  /** Whether to show as dropdown or inline buttons */
  layout?: "dropdown" | "inline";
  /** Maximum number of inline actions before using dropdown */
  maxInlineActions?: number;
}

const AssetActions: React.FC<AssetActionsProps> = ({
  asset,
  actions = [],
  variant = "default",
  layout = "inline",
  maxInlineActions = 3,
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  // Default actions based on variant
  const getDefaultActions = (): AssetAction[] => {
    const defaultActions: AssetAction[] = [];

    // Common actions
    defaultActions.push({
      label: "View",
      icon: faEye,
      onClick: () => console.log("View asset:", asset),
      variant: "ghost",
      className: "text-green-400 hover:text-green-300",
    });

    defaultActions.push({
      label: "Edit",
      icon: faEdit,
      onClick: () => console.log("Edit asset:", asset),
      variant: "ghost",
      className: "text-yellow-400 hover:text-yellow-300",
    });

    // Campaign-specific actions
    if (variant === "campaign") {
      defaultActions.push({
        label: "Manage Access",
        icon: faUsers,
        onClick: () => console.log("Manage access:", asset),
        variant: "ghost",
        className: "text-blue-400 hover:text-blue-300",
      });
    }

    // Tree-specific actions
    if (variant === "tree") {
      defaultActions.push({
        label: "Add Child",
        icon: faPlus,
        onClick: () => console.log("Add child to:", asset),
        variant: "ghost",
        className: "text-green-400 hover:text-green-300",
      });
    }

    return defaultActions;
  };

  const allActions = [...getDefaultActions(), ...actions];

  // Determine if we should use dropdown
  const shouldUseDropdown = layout === "dropdown" || allActions.length > maxInlineActions;

  if (shouldUseDropdown) {
    return (
      <div className="relative">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsDropdownOpen(!isDropdownOpen)}
          className="text-gray-400 hover:text-white p-2"
          aria-label="Asset actions"
        >
          <FontAwesomeIcon icon={faEllipsisV} />
        </Button>

        {isDropdownOpen && (
          <>
            {/* Backdrop */}
            <div className="fixed inset-0 z-30" onClick={() => setIsDropdownOpen(false)} />

            {/* Dropdown Menu */}
            <div className="absolute right-0 top-full mt-1 w-48 bg-gray-700 border border-gray-600 rounded-lg shadow-lg z-40">
              <div className="py-1">
                {allActions.map((action, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      action.onClick();
                      setIsDropdownOpen(false);
                    }}
                    disabled={action.disabled}
                    className={`
                      w-full px-4 py-2 text-left text-sm transition-colors
                      ${
                        action.disabled
                          ? "text-gray-500 cursor-not-allowed"
                          : "text-gray-300 hover:bg-gray-600 hover:text-white"
                      }
                      ${action.variant === "danger" ? "hover:bg-red-600 hover:text-white" : ""}
                    `}
                  >
                    <div className="flex items-center gap-2">
                      {action.icon && <FontAwesomeIcon icon={action.icon} className="w-4 h-4" />}
                      <span>{action.label}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </>
        )}
      </div>
    );
  }

  // Inline actions
  return (
    <div className="flex items-center gap-1">
      {allActions.map((action, index) => (
        <Button
          key={index}
          variant={action.variant || "ghost"}
          size="sm"
          onClick={action.onClick}
          disabled={action.disabled}
          className={`p-2 ${action.className || "text-gray-400 hover:text-white"}`}
          title={action.label}
        >
          {action.icon && <FontAwesomeIcon icon={action.icon} />}
        </Button>
      ))}
    </div>
  );
};

export default AssetActions;
